/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../App.css';
import { getCourseDetail, updateCourse } from '../../api';
import RichTextEditor from '../../components/RichTextEditor';

const EditCourse = () => {
    const navigate = useNavigate();
    const { courseId } = useParams();
    const [courseData, setCourseData] = useState({
        name: '',
        description: '',
        level: 'beginner',
        image: '',
        chapters: []
    });
    const [expandedChapters, setExpandedChapters] = useState({});
    const [expandedLessons, setExpandedLessons] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
            return;
        }

        if (courseId) {
            fetchCourseDetail();
        }
    }, [courseId, navigate]);

    const fetchCourseDetail = async () => {
        try {
            setFetching(true);
            setError('');
            const data = await getCourseDetail(courseId);

            // Ensure all nested arrays exist and have proper structure
            const formattedData = {
                ...data,
                chapters: (data.chapters || []).map(chapter => ({
                    ...chapter,
                    lessons: (chapter.lessons || []).map(lesson => ({
                        ...lesson,
                        fragments: (lesson.fragments || []).map(fragment => ({
                            ...fragment,
                            image_list: fragment.image_list || [],
                            answers: fragment.answers || null,
                            options: fragment.options || null,
                            correct_order: fragment.correct_order || '',
                            words: fragment.words || null,
                            translations: fragment.translations || null
                        }))
                    }))
                }))
            };

            setCourseData(formattedData);

            // Start with all chapters collapsed (accordion behavior)
            const expandedChaptersObj = {};
            const expandedLessonsObj = {};
            // Only expand first chapter by default
            if (formattedData.chapters.length > 0) {
                expandedChaptersObj[0] = true;
            }
            setExpandedChapters(expandedChaptersObj);
            setExpandedLessons(expandedLessonsObj);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load course details.');
            console.error('Error fetching course:', err);
        } finally {
            setFetching(false);
        }
    };

    const token = localStorage.getItem('token');
    if (!token) {
        return null;
    }

    const addChapter = () => {
        const newChapter = {
            chapter_index: courseData.chapters.length + 1,
            chapter_name: '',
            lessons: []
        };
        setCourseData({
            ...courseData,
            chapters: [...courseData.chapters, newChapter]
        });
    };

    const removeChapter = (chapterIndex) => {
        const updatedChapters = courseData.chapters
            .filter((_, idx) => idx !== chapterIndex)
            .map((chapter, idx) => ({
                ...chapter,
                chapter_index: idx + 1
            }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateChapter = (chapterIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex] = {
            ...updatedChapters[chapterIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addLesson = (chapterIndex) => {
        const chapter = courseData.chapters[chapterIndex];
        const newLesson = {
            lessons_index: chapter.lessons.length + 1,
            start_title: '',
            start_description: '',
            start_image: '',
            fragments: []
        };
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons = [...chapter.lessons, newLesson];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeLesson = (chapterIndex, lessonIndex) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons = updatedChapters[chapterIndex].lessons
            .filter((_, idx) => idx !== lessonIndex)
            .map((lesson, idx) => ({
                ...lesson,
                lessons_index: idx + 1
            }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateLesson = (chapterIndex, lessonIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex] = {
            ...updatedChapters[chapterIndex].lessons[lessonIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addFragment = (chapterIndex, lessonIndex) => {
        const lesson = courseData.chapters[chapterIndex].lessons[lessonIndex];
        const newFragment = {
            fragment_index: lesson.fragments.length + 1,
            type: 'text',
            title: '',
            content: '',
            image_list: []
        };
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments = [
            ...lesson.fragments,
            newFragment
        ];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments =
            updatedChapters[chapterIndex].lessons[lessonIndex].fragments
                .filter((_, idx) => idx !== fragmentIndex)
                .map((fragment, idx) => ({
                    ...fragment,
                    fragment_index: idx + 1
                }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const moveFragment = (chapterIndex, lessonIndex, fragmentIndex, direction) => {
        const updatedChapters = [...courseData.chapters];
        const fragments = [...updatedChapters[chapterIndex].lessons[lessonIndex].fragments];
        
        if (direction === -1 && fragmentIndex > 0) {
            // Move up
            const temp = fragments[fragmentIndex];
            fragments[fragmentIndex] = fragments[fragmentIndex - 1];
            fragments[fragmentIndex - 1] = temp;
        } else if (direction === 1 && fragmentIndex < fragments.length - 1) {
            // Move down
            const temp = fragments[fragmentIndex];
            fragments[fragmentIndex] = fragments[fragmentIndex + 1];
            fragments[fragmentIndex + 1] = temp;
        } else {
            return;
        }

        updatedChapters[chapterIndex].lessons[lessonIndex].fragments = fragments.map((fragment, idx) => ({
            ...fragment,
            fragment_index: idx + 1
        }));

        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragment = (chapterIndex, lessonIndex, fragmentIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex] = {
            ...updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addImageToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.image_list = [...(fragment.image_list || []), ''];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragmentImage = (chapterIndex, lessonIndex, fragmentIndex, imageIndex, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        if (!fragment.image_list) {
            fragment.image_list = [];
        }
        fragment.image_list[imageIndex] = value;
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragmentImage = (chapterIndex, lessonIndex, fragmentIndex, imageIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.image_list = fragment.image_list.filter((_, idx) => idx !== imageIndex);
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addAnswerToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        if (!fragment.answers) fragment.answers = [];
        fragment.answers = [...fragment.answers, { text: '', isRight: false }];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragmentAnswer = (chapterIndex, lessonIndex, fragmentIndex, answerIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.answers[answerIndex] = {
            ...fragment.answers[answerIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragmentAnswer = (chapterIndex, lessonIndex, fragmentIndex, answerIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.answers = fragment.answers.filter((_, idx) => idx !== answerIndex);
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addOptionToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        if (!fragment.options) fragment.options = [];
        fragment.options = [...fragment.options, { text: '', position: fragment.options.length + 1 }];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragmentOption = (chapterIndex, lessonIndex, fragmentIndex, optionIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.options[optionIndex] = {
            ...fragment.options[optionIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragmentOption = (chapterIndex, lessonIndex, fragmentIndex, optionIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.options = fragment.options.filter((_, idx) => idx !== optionIndex);
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addWordToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        if (!fragment.words) fragment.words = [];
        fragment.words = [...fragment.words, { word: '', position: fragment.words.length + 1 }];
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const updateFragmentWord = (chapterIndex, lessonIndex, fragmentIndex, wordIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.words[wordIndex] = {
            ...fragment.words[wordIndex],
            [field]: value
        };
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const removeFragmentWord = (chapterIndex, lessonIndex, fragmentIndex, wordIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.words = fragment.words.filter((_, idx) => idx !== wordIndex);
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const addTranslationToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        if (!fragment.translations) fragment.translations = [];
        fragment.translations = [...fragment.translations, { translation: '', position: fragment.translations.length + 1 }];
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const updateFragmentTranslation = (chapterIndex, lessonIndex, fragmentIndex, transIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.translations[transIndex] = {
            ...fragment.translations[transIndex],
            [field]: value
        };
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const removeFragmentTranslation = (chapterIndex, lessonIndex, fragmentIndex, transIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.translations = fragment.translations.filter((_, idx) => idx !== transIndex);
        setCourseData({ ...courseData, chapters: updatedChapters });
    };

    const toggleChapter = (chapterIndex) => {
        // Accordion behavior: if expanding this chapter, collapse all others
        if (!expandedChapters[chapterIndex]) {
            const newExpanded = {};
            newExpanded[chapterIndex] = true;
            setExpandedChapters(newExpanded);
        } else {
            // If collapsing, just collapse this one
            setExpandedChapters({
                ...expandedChapters,
                [chapterIndex]: false
            });
        }
    };

    const toggleLesson = (chapterIndex, lessonIndex) => {
        const key = `${chapterIndex}-${lessonIndex}`;
        setExpandedLessons({
            ...expandedLessons,
            [key]: !expandedLessons[key]
        });
    };

    const isContentEmpty = (content) => {
        if (!content) return true;
        const text = content.replace(/<[^>]*>/g, '').trim();
        return text === '' || text === '\n';
    };

    const validateCourseData = () => {
        for (const chapter of courseData.chapters) {
            for (const lesson of chapter.lessons) {
                for (const fragment of lesson.fragments) {
                    if (isContentEmpty(fragment.content)) {
                        return 'All fragments must have content. Please fill in the content field for all fragments.';
                    }
                }
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const validationError = validateCourseData();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            await updateCourse(courseId, courseData);
            alert('Course updated successfully!');
            navigate('/course-list');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update course.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '14px',
        border: '1px solid #cfd8dc',
        background: '#e5eff8',
        fontSize: '16px',
        color: '#0c1415',
        marginBottom: '12px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '8px',
        color: '#3c5a5d',
        fontWeight: '600',
        fontSize: '14px'
    };

    const sectionStyle = {
        background: '#F6F6F6',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #cfd8dc'
    };

    // Chapter style - Blue/Teal theme
    const chapterSectionStyle = {
        background: '#e0f2fe',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        border: '2px solid #0ea5e9'
    };

    // Lesson style - Green theme
    const lessonSectionStyle = {
        background: '#f0fdf4',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '12px',
        border: '2px solid #22c55e'
    };

    // Fragment style - Purple/Pink theme
    const fragmentSectionStyle = {
        background: '#faf5ff',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        border: '2px solid #a855f7'
    };

    if (fetching) {
        return (
            <div style={{
                minHeight: '100vh',
                background: '#ccdff5',
                padding: '40px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{
                    textAlign: 'center',
                    color: '#707F81',
                    fontSize: '1.1rem'
                }}>
                    Loading course details...
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '30px'
                }}>
                    <h1 style={{
                        color: '#3c5a5d',
                        margin: 0,
                        fontSize: '2.5rem'
                    }}>
                        Edit Course
                    </h1>
                    <button
                        onClick={() => navigate('/course-list')}
                        className="btn btn-secondary"
                        style={{ padding: '12px 24px' }}
                    >
                        ← Back to List
                    </button>
                </div>

                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '20px',
                        padding: '12px',
                        background: '#fee2e2',
                        borderRadius: '8px',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Course Level Fields */}
                    <div style={sectionStyle}>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px' }}>Course Information</h2>

                        <label style={labelStyle}>Course Name *</label>
                        <input
                            type="text"
                            value={courseData.name}
                            onChange={(e) => setCourseData({ ...courseData, name: e.target.value })}
                            required
                            style={inputStyle}
                            placeholder="Enter course name"
                        />

                        <label style={labelStyle}>Description *</label>
                        <textarea
                            value={courseData.description}
                            onChange={(e) => setCourseData({ ...courseData, description: e.target.value })}
                            required
                            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                            placeholder="Enter course description"
                        />

                        <label style={labelStyle}>Level *</label>
                        <select
                            value={courseData.level}
                            onChange={(e) => setCourseData({ ...courseData, level: e.target.value })}
                            required
                            style={inputStyle}
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>

                        <label style={labelStyle}>Course Image URL</label>
                        <input
                            type="text"
                            value={courseData.image || ''}
                            onChange={(e) => setCourseData({ ...courseData, image: e.target.value })}
                            style={inputStyle}
                            placeholder="Enter image URL"
                        />
                    </div>

                    {/* Chapters */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
                            <h2 style={{ color: '#3c5a5d', margin: 0 }}>Chapters</h2>
                            <button
                                type="button"
                                onClick={addChapter}
                                className="btn btn-secondary"
                                style={{ padding: '10px 20px' }}
                            >
                                + Add Chapter
                            </button>
                        </div>

                        {courseData.chapters.map((chapter, chapterIndex) => (
                            <div key={chapterIndex} style={chapterSectionStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h3 style={{ color: '#0369a1', margin: 0, fontWeight: '700' }}>
                                        Chapter {chapter.chapter_index}
                                    </h3>
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => toggleChapter(chapterIndex)}
                                            className="btn btn-secondary"
                                            style={{ padding: '8px 16px', marginRight: '8px', fontSize: '14px' }}
                                        >
                                            {expandedChapters[chapterIndex] ? '▼ Collapse' : '▶ Expand'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeChapter(chapterIndex)}
                                            className="btn btn-secondary"
                                            style={{ padding: '8px 16px', fontSize: '14px', background: '#fee2e2', color: '#dc2626' }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>

                                {expandedChapters[chapterIndex] && (
                                    <>
                                        <label style={{ ...labelStyle, color: '#0369a1' }}>Chapter Name *</label>
                                        <input
                                            type="text"
                                            value={chapter.chapter_name || ''}
                                            onChange={(e) => updateChapter(chapterIndex, 'chapter_name', e.target.value)}
                                            required
                                            style={inputStyle}
                                            placeholder="Enter chapter name"
                                        />

                                        {/* Lessons */}
                                        <div style={{ marginTop: '20px' }}>
                                            <h4 style={{ color: '#15803d', margin: '0 0 12px 0', fontWeight: '600' }}>Lessons</h4>

                                            {chapter.lessons.map((lesson, lessonIndex) => {
                                                const lessonKey = `${chapterIndex}-${lessonIndex}`;
                                                return (
                                                    <div key={lessonIndex} style={lessonSectionStyle}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <h5 style={{ color: '#15803d', margin: 0, fontWeight: '600' }}>
                                                                Lesson {lesson.lessons_index}
                                                                {!expandedLessons[lessonKey] && lesson.start_title && (
                                                                    <span style={{ marginLeft: '8px', fontWeight: '400', color: '#15803d', opacity: 0.8 }}>
                                                                        - {lesson.start_title}
                                                                    </span>
                                                                )}
                                                            </h5>
                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => window.open(`/lessons-preview`, '_blank', 'noopener,noreferrer')}
                                                                    onMouseDown={() => {
                                                                        // Store fragments in localStorage to pass to new tab
                                                                        localStorage.setItem('previewFragments', JSON.stringify(lesson.fragments));
                                                                    }}
                                                                    className="btn btn-secondary"
                                                                    style={{ padding: '6px 12px', marginRight: '8px', fontSize: '12px', background: '#e0e7ff', color: '#4f46e5' }}
                                                                >
                                                                    Preview
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleLesson(chapterIndex, lessonIndex)}
                                                                    className="btn btn-secondary"
                                                                    style={{ padding: '6px 12px', marginRight: '8px', fontSize: '12px' }}
                                                                >
                                                                    {expandedLessons[lessonKey] ? '▼ Collapse' : '▶ Expand'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeLesson(chapterIndex, lessonIndex)}
                                                                    className="btn btn-secondary"
                                                                    style={{ padding: '6px 12px', fontSize: '12px', background: '#fee2e2', color: '#dc2626' }}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {expandedLessons[lessonKey] && (
                                                            <>
                                                                <label style={{ ...labelStyle, color: '#15803d' }}>Start Title *</label>
                                                                <input
                                                                    type="text"
                                                                    value={lesson.start_title || ''}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_title', e.target.value)}
                                                                    required
                                                                    style={inputStyle}
                                                                    placeholder="Enter lesson start title"
                                                                />

                                                                <label style={{ ...labelStyle, color: '#15803d' }}>Start Description *</label>
                                                                <textarea
                                                                    value={lesson.start_description || ''}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_description', e.target.value)}
                                                                    required
                                                                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                                                                    placeholder="Enter lesson start description"
                                                                />

                                                                <label style={{ ...labelStyle, color: '#15803d' }}>Start Image URL</label>
                                                                <input
                                                                    type="text"
                                                                    value={lesson.start_image || ''}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_image', e.target.value)}
                                                                    style={inputStyle}
                                                                    placeholder="Enter start image URL"
                                                                />

                                                                {/* Fragments */}
                                                                <div style={{ marginTop: '16px' }}>
                                                                    <h6 style={{ color: '#7c3aed', margin: '0 0 12px 0', fontWeight: '600' }}>Fragments</h6>

                                                                    {lesson.fragments.map((fragment, fragmentIndex) => (
                                                                        <div key={fragmentIndex} style={fragmentSectionStyle}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                                    <span style={{ color: '#7c3aed', fontWeight: '600' }}>
                                                                                        Fragment {fragment.fragment_index}
                                                                                    </span>
                                                                                    <div style={{ display: 'flex', gap: '4px', marginLeft: '12px' }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => moveFragment(chapterIndex, lessonIndex, fragmentIndex, -1)}
                                                                                            disabled={fragmentIndex === 0}
                                                                                            style={{ padding: '2px 8px', borderRadius: '4px', background: fragmentIndex === 0 ? '#e5e7eb' : '#ede9fe', color: fragmentIndex === 0 ? '#9ca3af' : '#7c3aed', border: 'none', cursor: fragmentIndex === 0 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                                                                                        >
                                                                                            ↑
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => moveFragment(chapterIndex, lessonIndex, fragmentIndex, 1)}
                                                                                            disabled={fragmentIndex === lesson.fragments.length - 1}
                                                                                            style={{ padding: '2px 8px', borderRadius: '4px', background: fragmentIndex === lesson.fragments.length - 1 ? '#e5e7eb' : '#ede9fe', color: fragmentIndex === lesson.fragments.length - 1 ? '#9ca3af' : '#7c3aed', border: 'none', cursor: fragmentIndex === lesson.fragments.length - 1 ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                                                                                        >
                                                                                            ↓
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => removeFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                    className="btn btn-secondary"
                                                                                    style={{ padding: '4px 10px', fontSize: '11px', background: '#fee2e2', color: '#dc2626' }}
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </div>

                                                                            <label style={{ ...labelStyle, color: '#7c3aed' }}>Type *</label>
                                                                            <select
                                                                                value={fragment.type || 'text'}
                                                                                onChange={(e) => {
                                                                                    updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'type', e.target.value);
                                                                                    const currentFragment = courseData.chapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
                                                                                    if (e.target.value === 'test' || e.target.value === 'true_or_false' || e.target.value === 'finish_sentence' || e.target.value === 'build_sentence') {
                                                                                        if (!currentFragment.answers) {
                                                                                            updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'answers', []);
                                                                                        }
                                                                                    }
                                                                                    if (e.target.value === 'build_sentence') {
                                                                                        if (currentFragment.correct_order === undefined) {
                                                                                            updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'correct_order', '');
                                                                                        }
                                                                                    }
                                                                                    if (e.target.value === 'match_pairs') {
                                                                                        if (!currentFragment.words) {
                                                                                            updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'words', []);
                                                                                        }
                                                                                        if (!currentFragment.translations) {
                                                                                            updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'translations', []);
                                                                                        }
                                                                                    }
                                                                                }}
                                                                                required
                                                                                style={inputStyle}
                                                                            >
                                                                                <option value="text">Text</option>
                                                                                <option value="practice">Practice</option>
                                                                                <option value="test">Test/Quiz</option>
                                                                                <option value="true_or_false">True or False</option>
                                                                                <option value="build_sentence">Build Sentence</option>
                                                                                <option value="finish_sentence">Finish Sentence</option>
                                                                                <option value="match_pairs">Match Pairs</option>
                                                                            </select>

                                                                            <label style={{ ...labelStyle, color: '#7c3aed' }}>Title</label>
                                                                            <input
                                                                                type="text"
                                                                                value={fragment.title || ''}
                                                                                onChange={(e) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'title', e.target.value)}
                                                                                style={inputStyle}
                                                                                placeholder="Enter fragment title (optional)"
                                                                            />

                                                                            <label style={{ ...labelStyle, color: '#7c3aed' }}>Content *</label>
                                                                            <RichTextEditor
                                                                                value={fragment.content || ''}
                                                                                onChange={(value) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'content', value)}
                                                                                placeholder="Enter fragment content"
                                                                                required={true}
                                                                            />

                                                                            <div style={{ marginTop: '12px' }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                                    <label style={{ ...labelStyle, color: '#7c3aed' }}>Image List</label>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => addImageToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                        className="btn btn-secondary"
                                                                                        style={{ padding: '4px 10px', fontSize: '11px' }}
                                                                                    >
                                                                                        + Add Image
                                                                                    </button>
                                                                                </div>
                                                                                {(fragment.image_list || []).map((image, imageIndex) => (
                                                                                    <div key={imageIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={image || ''}
                                                                                            onChange={(e) => updateFragmentImage(chapterIndex, lessonIndex, fragmentIndex, imageIndex, e.target.value)}
                                                                                            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                            placeholder="Enter image URL"
                                                                                        />
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => removeFragmentImage(chapterIndex, lessonIndex, fragmentIndex, imageIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '12px 16px', background: '#fee2e2', color: '#dc2626' }}
                                                                                        >
                                                                                            ×
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>

                                                                            {(fragment.type === 'test' || fragment.type === 'true_or_false' || fragment.type === 'finish_sentence') && (
                                                                                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(124, 58, 237, 0.05)', borderRadius: '8px', border: '1px solid rgba(124, 58, 237, 0.2)' }}>
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                                                        <label style={{ ...labelStyle, color: '#7c3aed', marginBottom: 0 }}>Quiz Answers</label>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => addAnswerToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#7c3aed', color: 'white', border: 'none' }}
                                                                                        >
                                                                                            + Add Answer
                                                                                        </button>
                                                                                    </div>
                                                                                    {(fragment.answers || []).map((answer, answerIndex) => (
                                                                                        <div key={answerIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                                                            <input
                                                                                                type="text"
                                                                                                value={answer.text || ''}
                                                                                                onChange={(e) => updateFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex, 'text', e.target.value)}
                                                                                                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                                placeholder="Enter answer text"
                                                                                            />
                                                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', margin: 0, color: '#3c5a5d', fontSize: '14px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                                                                                <input
                                                                                                    type="checkbox"
                                                                                                    checked={answer.isRight || false}
                                                                                                    onChange={(e) => updateFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex, 'isRight', e.target.checked)}
                                                                                                    style={{ width: '16px', height: '16px', margin: 0 }}
                                                                                                />
                                                                                                Correct
                                                                                            </label>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => removeFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex)}
                                                                                                className="btn btn-secondary"
                                                                                                style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px' }}
                                                                                            >
                                                                                                ×
                                                                                            </button>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}

                                                                            {fragment.type === 'build_sentence' && (
                                                                                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(59, 130, 246, 0.05)', borderRadius: '8px', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                                                                    <div style={{ marginBottom: '12px' }}>
                                                                                        <label style={{ ...labelStyle, color: '#2563eb' }}>Correct Order *</label>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={fragment.correct_order || ''}
                                                                                            onChange={(e) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'correct_order', e.target.value)}
                                                                                            style={inputStyle}
                                                                                            placeholder="e.g. 1,2,3,4 or 1-2-3-4"
                                                                                        />
                                                                                    </div>

                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                                                        <label style={{ ...labelStyle, color: '#2563eb', marginBottom: 0 }}>Sentence Options</label>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => addAnswerToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#2563eb', color: 'white', border: 'none' }}
                                                                                        >
                                                                                            + Add Option
                                                                                        </button>
                                                                                    </div>
                                                                                    {(fragment.answers || []).map((answer, answerIndex) => (
                                                                                        <div key={answerIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={answer.position === undefined ? '' : answer.position}
                                                                                                onChange={(e) => updateFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex, 'position', parseInt(e.target.value))}
                                                                                                style={{ ...inputStyle, marginBottom: 0, width: '80px' }}
                                                                                                placeholder="Pos"
                                                                                            />
                                                                                            <input
                                                                                                type="text"
                                                                                                value={answer.text || ''}
                                                                                                onChange={(e) => updateFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex, 'text', e.target.value)}
                                                                                                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                                placeholder="Enter word or phrase"
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => removeFragmentAnswer(chapterIndex, lessonIndex, fragmentIndex, answerIndex)}
                                                                                                className="btn btn-secondary"
                                                                                                style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px' }}
                                                                                            >
                                                                                                ×
                                                                                            </button>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}

                                                                            {fragment.type === 'match_pairs' && (
                                                                                <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(234, 179, 8, 0.05)', borderRadius: '8px', border: '1px solid rgba(234, 179, 8, 0.2)' }}>
                                                                                    {/* Words Section */}
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                                                        <label style={{ ...labelStyle, color: '#ca8a04', marginBottom: 0 }}>Words</label>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => addWordToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#ca8a04', color: 'white', border: 'none' }}
                                                                                        >
                                                                                            + Add Word
                                                                                        </button>
                                                                                    </div>
                                                                                    {(fragment.words || []).map((word, wordIndex) => (
                                                                                        <div key={`word-${wordIndex}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={word.position === undefined ? '' : word.position}
                                                                                                onChange={(e) => updateFragmentWord(chapterIndex, lessonIndex, fragmentIndex, wordIndex, 'position', parseInt(e.target.value))}
                                                                                                style={{ ...inputStyle, marginBottom: 0, width: '80px' }}
                                                                                                placeholder="Match ID"
                                                                                            />
                                                                                            <input
                                                                                                type="text"
                                                                                                value={word.word || ''}
                                                                                                onChange={(e) => updateFragmentWord(chapterIndex, lessonIndex, fragmentIndex, wordIndex, 'word', e.target.value)}
                                                                                                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                                placeholder="Enter word"
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => removeFragmentWord(chapterIndex, lessonIndex, fragmentIndex, wordIndex)}
                                                                                                className="btn btn-secondary"
                                                                                                style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px' }}
                                                                                            >
                                                                                                ×
                                                                                            </button>
                                                                                        </div>
                                                                                    ))}

                                                                                    {/* Translations Section */}
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', marginTop: '16px' }}>
                                                                                        <label style={{ ...labelStyle, color: '#ca8a04', marginBottom: 0 }}>Translations</label>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => addTranslationToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '4px 10px', fontSize: '11px', background: '#ca8a04', color: 'white', border: 'none' }}
                                                                                        >
                                                                                            + Add Translation
                                                                                        </button>
                                                                                    </div>
                                                                                    {(fragment.translations || []).map((translation, transIndex) => (
                                                                                        <div key={`trans-${transIndex}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                                                                                            <input
                                                                                                type="number"
                                                                                                value={translation.position === undefined ? '' : translation.position}
                                                                                                onChange={(e) => updateFragmentTranslation(chapterIndex, lessonIndex, fragmentIndex, transIndex, 'position', parseInt(e.target.value))}
                                                                                                style={{ ...inputStyle, marginBottom: 0, width: '80px' }}
                                                                                                placeholder="Match ID"
                                                                                            />
                                                                                            <input
                                                                                                type="text"
                                                                                                value={translation.translation || ''}
                                                                                                onChange={(e) => updateFragmentTranslation(chapterIndex, lessonIndex, fragmentIndex, transIndex, 'translation', e.target.value)}
                                                                                                style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                                placeholder="Enter translation"
                                                                                            />
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => removeFragmentTranslation(chapterIndex, lessonIndex, fragmentIndex, transIndex)}
                                                                                                className="btn btn-secondary"
                                                                                                style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '8px' }}
                                                                                            >
                                                                                                ×
                                                                                            </button>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addFragment(chapterIndex, lessonIndex)}
                                                                        className="btn btn-secondary"
                                                                        style={{
                                                                            padding: '8px 16px',
                                                                            fontSize: '14px',
                                                                            width: '100%',
                                                                            marginTop: '8px',
                                                                            background: '#a855f7',
                                                                            color: 'white',
                                                                            border: 'none'
                                                                        }}
                                                                    >
                                                                        + Add Fragment
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            <button
                                                type="button"
                                                onClick={() => addLesson(chapterIndex)}
                                                className="btn btn-secondary"
                                                style={{
                                                    padding: '10px 20px',
                                                    fontSize: '14px',
                                                    width: '100%',
                                                    marginTop: '12px',
                                                    background: '#22c55e',
                                                    color: 'white',
                                                    border: 'none'
                                                }}
                                            >
                                                + Add Lesson
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                        <button
                            type="button"
                            onClick={() => navigate('/course-list')}
                            className="btn btn-secondary"
                            style={{
                                flex: 1,
                                padding: '15px',
                                fontSize: '18px'
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="btn btn-primary"
                            style={{
                                flex: 1,
                                padding: '15px',
                                fontSize: '18px'
                            }}
                        >
                            {loading ? 'Updating Course...' : 'Update Course'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default EditCourse;
